import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core, Decorative } from "@faire/design-tokens";
import { useInsiderState } from "@faire/retailer-visitor-shared/hooks/insider/useInsiderState";
import { useMilestone } from "@faire/retailer-visitor-shared/stores/domain/useMilestone";
import { useUserStore } from "@faire/retailer-visitor-shared/stores/domain/UserStore";
import { getCurrentPath } from "@faire/web--source/common/Path";
import { Button } from "@faire/web--source/slate/Button";
import { Insider } from "@faire/web--source/slate/icons/InsiderIcon";
import { Row } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Tooltip } from "@faire/web--source/slate/Tooltip";
import { Typography } from "@faire/web--source/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { trackInsiderBrandPageHeaderEnrollClick } from "@faire/web-api--source/events/insider/click/brandPageHeaderEnroll";
import { trackInsiderBrandPageHeaderResumeClick } from "@faire/web-api--source/events/insider/click/brandPageHeaderResume";
import { trackRetailerTeamManagementInsiderMarketingPageLearnMoreRedirectClick } from "@faire/web-api--source/events/retailerTeamManagement/click/insiderMarketingPageLearnMoreRedirect";
import { INSIDER_BADGE_TOOLTIP_SEEN } from "@faire/web-api--source/indigofair/data/IUserMilestone/RetailerMilestone/INSIDER_BADGE_TOOLTIP_SEEN";
import { INSIDER_THRESHOLD_BADGE_TOOLTIP_SEEN } from "@faire/web-api--source/indigofair/data/IUserMilestone/RetailerMilestone/INSIDER_THRESHOLD_BADGE_TOOLTIP_SEEN";
import { IInsiderModalRequest } from "@faire/web-api--source/indigofair/insider/IInsiderModalRequest";
import insiderMarketingPage from "@faire/web-api--source/routes/user/insider/info";
import React from "react";
import styled from "styled-components";
/**
 * BE Driven Insider Benefits label component
 */ export var InsiderLabel = function(param) {
    var className = param.className, insiderLabel = param.insiderLabel, tooltipPosition = param.tooltipPosition, _param_context = param.context, context = _param_context === void 0 ? IInsiderModalRequest.InsiderModalContext.BRAND_PAGE : _param_context;
    var isTabletAndAbove = useViewport().isTabletAndAbove;
    if (!insiderLabel) {
        return null;
    }
    var text = insiderLabel.text, cta_text = insiderLabel.cta_text, tooltip_message = insiderLabel.tooltip_message;
    var showTooltip = tooltip_message && isTabletAndAbove;
    return /*#__PURE__*/ _jsxs(Row, {
        className: className,
        gap: 4,
        "data-test-id": "insider-label",
        children: [
            /*#__PURE__*/ _jsx(StyledInsider, {
                small: true,
                color: Decorative.icon.teal,
                fill: Decorative.icon.teal,
                fontSize: "16px"
            }),
            /*#__PURE__*/ _jsxs(Typography, {
                variant: "paragraphSansMedium",
                color: Decorative.icon.teal,
                children: [
                    text,
                    cta_text ? /*#__PURE__*/ _jsx(CtaText, {
                        text: cta_text,
                        context: context
                    }) : null
                ]
            }),
            showTooltip ? /*#__PURE__*/ _jsx(LabelTooltip, {
                tooltipPosition: tooltipPosition,
                insiderLabel: insiderLabel
            }) : null
        ]
    });
};
var LabelTooltip = function(param) {
    var tooltipPosition = param.tooltipPosition, insiderLabel = param.insiderLabel;
    var isInsiderActive = useInsiderState().isInsiderActive;
    var _useMilestone = useMilestone(INSIDER_THRESHOLD_BADGE_TOOLTIP_SEEN), hasSeenThresholdBadgeTooltip = _useMilestone.hasOccurred, setHasSeenThresholdBadgeTooltip = _useMilestone.setHasOccurred;
    var _useMilestone1 = useMilestone(INSIDER_BADGE_TOOLTIP_SEEN), hasSeenInsiderBadgeTooltip = _useMilestone1.hasOccurred, setHasSeenInsiderBadgeTooltip = _useMilestone1.setHasOccurred;
    var tooltipRef = useDoActionWhenVisible(function() {
        if (isInsiderActive) {
            if (!hasSeenThresholdBadgeTooltip && (insiderLabel === null || insiderLabel === void 0 ? void 0 : insiderLabel.tooltip_title)) {
                setHasSeenThresholdBadgeTooltip();
            } else if (!hasSeenInsiderBadgeTooltip) {
                setHasSeenInsiderBadgeTooltip();
            }
        }
    });
    if (!insiderLabel) {
        return null;
    }
    var tooltip_message = insiderLabel.tooltip_message, tooltip_cta_text = insiderLabel.tooltip_cta_text, tooltip_cta_url = insiderLabel.tooltip_cta_url, tooltip_title = insiderLabel.tooltip_title;
    return /*#__PURE__*/ _jsxs(StyledTooltip, {
        iconColor: Core.icon.subdued,
        position: tooltipPosition,
        innerRef: tooltipRef,
        children: [
            tooltip_title ? /*#__PURE__*/ _jsx(Typography, {
                color: Core.text.inverse,
                variant: "paragraphSansMedium",
                children: tooltip_title
            }) : null,
            /*#__PURE__*/ _jsx(Typography, {
                color: Core.text.inverse,
                variant: "paragraphSansRegular",
                children: tooltip_message
            }),
            tooltip_cta_text && tooltip_cta_url ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Spacer, {
                        height: "2x"
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        variant: "tertiary",
                        tertiaryColor: Core.text.inverse,
                        linkTo: tooltip_cta_url,
                        target: "_blank",
                        children: tooltip_cta_text
                    })
                ]
            }) : null
        ]
    });
};
var StyledInsider = styled(Insider).withConfig({
    componentId: "sc-1f570cd3-0"
})([
    "margin-top:3px;align-self:flex-start;"
]);
var CtaText = function(param) {
    var text = param.text, context = param.context;
    var isInsiderPaused = useInsiderState().isInsiderPaused;
    var userCanPlaceOrder = useUserStore([
        "userCanPlaceOrder"
    ]).userCanPlaceOrder;
    var linkTo = !userCanPlaceOrder ? insiderMarketingPage() : getCurrentPath().append({
        insiderEnrolment: context
    }).toString();
    var onClick = function() {
        if (!userCanPlaceOrder) {
            trackRetailerTeamManagementInsiderMarketingPageLearnMoreRedirectClick("insider-label");
        }
        if (isInsiderPaused) {
            trackInsiderBrandPageHeaderResumeClick();
        } else {
            trackInsiderBrandPageHeaderEnrollClick();
        }
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            " ",
            /*#__PURE__*/ _jsx(Button, {
                variant: "tertiary",
                linkTo: linkTo,
                onClick: onClick,
                "data-test-id": "insider-label-cta",
                children: text
            })
        ]
    });
};
var StyledTooltip = styled(Tooltip).withConfig({
    componentId: "sc-1f570cd3-1"
})([
    "vertical-align:text-top;"
]);
